import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Building2, Plus, Trash2, AlertCircle, ExternalLink } from 'lucide-react';
import { API_BASE } from '../config';

interface Watched {
  id: number;
  name: string;
  careers_url: string;
  ats_type: string | null;
  ats_slug: string | null;
  crawlable: number;
}

const WatchlistSettings: React.FC = () => {
  const [items, setItems] = useState<Watched[]>([]);
  const [max, setMax] = useState(10);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/watchlist`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setItems(d.watched ?? []);
        setMax(d.max ?? 10);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNote('');
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/watchlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ careers_url: url.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Could not add company');
      setNote(d.message || 'Added');
      setUrl('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not add company');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await fetch(`${API_BASE}/api/watchlist/${id}`, { method: 'DELETE', credentials: 'include' });
      await load();
    } catch {
      /* ignore */
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
    >
      <div className="p-6 border-b border-gray-200 dark:border-slate-800">
        <div className="flex items-center space-x-2">
          <Building2 className="w-5 h-5 text-gray-600 dark:text-slate-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Companies to Watch</h3>
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Paste a careers page URL. We auto-detect the job board; supported ones ({items.length}/{max}) are
          crawled automatically every day.
        </p>
      </div>

      <div className="p-6 space-y-4">
        <form onSubmit={add} className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://boards.greenhouse.io/acme"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
            required
          />
          <button
            type="submit"
            disabled={busy || items.length >= max}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span>{busy ? 'Adding…' : 'Watch'}</span>
          </button>
        </form>

        {error && (
          <div className="flex items-center space-x-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4" /><span>{error}</span>
          </div>
        )}
        {note && <p className="text-sm text-emerald-600 dark:text-emerald-400">{note}</p>}

        {items.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-slate-400">No companies watched yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-slate-800">
            {items.map((w) => (
              <li key={w.id} className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{w.name}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        w.crawlable
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}
                    >
                      {w.ats_type ?? 'unknown'}{w.crawlable ? ' · auto' : ' · manual'}
                    </span>
                  </div>
                  <a
                    href={w.careers_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-gray-500 dark:text-slate-400 hover:underline inline-flex items-center gap-1 truncate"
                  >
                    {w.careers_url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <button
                  onClick={() => remove(w.id)}
                  aria-label={`Remove ${w.name}`}
                  className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1 flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
};

export default WatchlistSettings;
